"use client";

import React, { useState } from 'react';
import clsx from 'clsx';
import { PrismicNextImage } from '@prismicio/next';
import { isFilled } from '@prismicio/helpers';
import { dashify, sliceStyle } from '@/app/lib/utils';
import { RevealTitle } from '@/app/lib/RevealTitle';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import PhotoAlbum from "react-photo-album";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Inline from "yet-another-react-lightbox/plugins/inline";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import galleryStyles from './gallery.module.scss';
import sharedStyles from '@/app/shared.module.scss';
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "react-photo-album/masonry.css";

const getOriginalImageUrl = (image) => {
	if (!image?.url) return '';

	return image.url.split('?')[0];
};

const Gallery = ({ slice }) => {
  const [index, setIndex] = useState(-1);

  const albumPhotos = slice?.primary?.images
    ?.filter(item => isFilled.image(item?.image))
    .map((item, photoIndex) => ({
      key: photoIndex,
      src: item.image.url,
      originalSrc: getOriginalImageUrl(item.image),
      download: getOriginalImageUrl(item.image),
      width: item.image.dimensions?.width,
      height: item.image.dimensions?.height,
      alt: item.image.alt || '',
      description: isFilled.keyText(item.description) ? item.description : undefined,
    })) || [];
  const lightboxSlides = albumPhotos.map((photo) => ({
    ...photo,
    src: photo.originalSrc || photo.src,
    download: photo.originalSrc || photo.download || photo.src,
  }));

  return (
    <BackgroundImage slicePrimary={slice?.primary}>
      <div className={clsx(galleryStyles.default, !isFilled.keyText(slice?.primary?.title) && galleryStyles.withPadding )} style={sliceStyle(slice?.primary)}>

        {/* title */}
        {slice?.primary?.title && 
        <div className={clsx(sharedStyles.top)}>
          <div className={clsx(sharedStyles.left)}>
            { isFilled.keyText(slice?.primary?.title) && (
              <h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>
                <RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
              </h1>
            )}
          </div>
        </div>
        }

        <div className={clsx(galleryStyles.photos)}>
          { slice?.primary?.style === 'Photo album' && albumPhotos.length > 0 && (
            <>
              <PhotoAlbum
                photos={albumPhotos}
                layout="masonry"
                columns={(containerWidth) => {
                  if (containerWidth < 480) return 1;
                  return 3;
                }}
                spacing={10}
                padding={0}
                onClick={({ index: currentAlbumIndex }) => setIndex(currentAlbumIndex)}
                renderPhoto={({ photo, wrapperStyle }) => (
                  <div style={wrapperStyle} key={photo.key}>
                    <PrismicNextImage
                      field={{ url: photo.src, alt: photo.alt }}
                      width={photo.width}
                      height={photo.height}
                      fallbackAlt=''
                      onClick={() => setIndex(photo.key)}
                      className={galleryStyles.image}
                    />
                  </div>
                )}
              />

              {index !== -1 && (
                <Lightbox
                  slides={lightboxSlides}
                  index={index}
                  open={index >= 0}
                  close={() => setIndex(-1)}
                  plugins={[Captions, Counter, Download, Fullscreen, Zoom]}
                  captions={{ descriptionTextAlign: 'start' }}
                  counter={{ container: { style: { top: "unset", bottom: 0 } } }}
                />
              )}
            </>
          )}

          { slice?.primary?.style === 'Carousel' && albumPhotos.length > 0 && (
            <Lightbox
              slides={lightboxSlides}
              index={index}
              inline={{style: {
                width: "100%",
                maxWidth: "100%",
                aspectRatio: "3 / 2",
                margin: "0 auto",
              }}}
              plugins={[Captions, Download, Fullscreen, Zoom, Inline, Thumbnails]}
              captions={{ descriptionTextAlign: 'center' }}
              counter={{ container: { style: { top: "unset", bottom: 0 } } }}
            />
          )}
        </div>
      </div>
    </BackgroundImage>
  );

};

export default Gallery;
